import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { delayCall } from "api/util";
import { Modal } from "components";

import Toast from "../toast/Toast";

import { Icon } from "@/design-system";

const ACCOUNT_ITEMS = [
  {
    label: "Orders & tracking",
    path: "/purchase-history",
    icon: "local_shipping",
  },
  { label: "Returns", path: "/returns", icon: "assignment_return" },
  { label: "Invoices & statements", path: "/invoices", icon: "description" },
  { label: "Saved lists", path: "/favorites", icon: "favorite" },
  { label: "Settings", path: "/profile/settings", icon: "settings" },
];

function getDisplayName(user) {
  const value = user?.given_name || user?.name || user?.email || "User";
  return String(value).split(/[\s@]/).filter(Boolean)[0] || "User";
}

function getInitials(user) {
  const value = user?.name || user?.email || "User";
  const parts = String(value)
    .split(/[\s@._-]+/)
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export default function AuthButton() {
  const { loginWithRedirect, logout, isAuthenticated, user, isLoading, error } =
    useAuth0();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (error?.message?.toLowerCase().includes("verify your email")) {
      Toast.error(
        "Please verify your email address to continue. Check your inbox for a verification link."
      );
      return delayCall(() => {
        logout({ returnTo: window.location.origin });
      }, 5000);
    }

    return undefined;
  }, [error, logout]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dropdownOpen]);

  if (isLoading) {
    return (
      <span
        className="inline-flex h-[42px] w-[108px] items-center justify-center rounded-pill border-[1.5px] border-line bg-card text-ink-3"
        role="status"
        aria-label="Loading account"
      >
        <span
          className="h-[16px] w-[16px] animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"
          aria-hidden="true"
        />
      </span>
    );
  }

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        className="inline-flex h-[42px] cursor-pointer items-center gap-[var(--space-2)] rounded-pill border-[1.5px] border-line bg-transparent px-[18px] py-0 font-sans text-sm font-semibold text-blue-700 [transition:background_var(--duration-fast),border-color_var(--duration-fast),color_var(--duration-fast)] hover:border-blue-400 hover:bg-soft focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        onClick={() => loginWithRedirect({ screen_hint: "login" })}
      >
        <Icon name="person" size={20} weight={400} />
        Sign in
      </button>
    );
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          onClick={() => setDropdownOpen((open) => !open)}
          className="flex cursor-pointer items-center gap-[var(--space-2)] rounded-md border-0 bg-transparent px-[10px] py-[6px] font-sans [transition:background_var(--duration-fast)] hover:bg-soft focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          <span className="inline-flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-md bg-blue-600 font-sans text-xs font-bold text-white">
            {getInitials(user)}
          </span>
          <span className="flex flex-col text-left leading-[1.2]">
            <small className="text-xs text-ink-3">
              Hi, {getDisplayName(user)}
            </small>
            <strong className="text-sm text-ink">Account</strong>
          </span>
          <Icon
            name="expand_more"
            size={18}
            weight={400}
            className={`text-ink-2 [transition:transform_var(--duration-base)_var(--ease-spring)] ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {dropdownOpen && !showModal && (
          <div
            role="menu"
            className="absolute right-0 top-[calc(100%_+_8px)] z-[var(--z-dropdown)] w-[268px] rounded-lg border border-line-subtle bg-card px-[var(--space-2)] py-[var(--space-2)] shadow-lg"
          >
            {ACCOUNT_ITEMS.map((item) => (
              <button
                key={item.path}
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center gap-[var(--space-3)] rounded-sm border-0 bg-transparent px-[var(--space-3)] py-[10px] text-left font-sans text-sm text-ink [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                onClick={() => {
                  setDropdownOpen(false);
                  navigate(item.path);
                }}
              >
                <Icon
                  name={item.icon}
                  size={20}
                  weight={400}
                  className="text-ink-2"
                />
                {item.label}
              </button>
            ))}
            <div className="mt-[6px] border-t border-line-subtle pt-[6px]">
              <button
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center gap-[var(--space-3)] rounded-sm border-0 bg-transparent px-[var(--space-3)] py-[10px] text-left font-sans text-sm text-ink [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-error-50 hover:text-error-600 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                onClick={() => {
                  setDropdownOpen(false);
                  setShowModal(true);
                }}
              >
                <Icon
                  name="logout"
                  size={20}
                  weight={400}
                  className="text-ink-2"
                />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <Modal
          title="Confirm Logout"
          image={null}
          onClose={() => setShowModal(false)}
          onSubmit={() => {
            setShowModal(false);
            logout({ returnTo: window.location.origin });
          }}
          onCloseText="Cancel"
          onSubmitText="Logout"
        />
      )}
    </>
  );
}
